import { getSelectedOwnerFromCookie } from '@industronics/fids-utils';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';

type Reason = 'unauthenticated' | 'expired' | 'logout';
export let accessToken: string | null = null;

export interface AuthPluginOptions {
  /** e.g. https://staging.auth.iax.my */
  authBaseURL: string;
  /** Where to send the user when unauthenticated (your app’s login page) */
  loginURL?: string; // e.g. https://staging.tams.iax.my/login
  /** Optional: dev override access token to skip refresh while developing */
  devStaticAccessToken?: string;
  /** Override default auth paths that skip refresh logic */
  skipPaths?: string[];
}
export interface AuthPlugin {
  /** Attach the auth interceptor to ANY axios instance (microservices, etc.) */
  attachAuthInterceptor(api: AxiosInstance): void;

  attachTaggingInterceptor(api: AxiosInstance): void;

  /** Mint an access token from the RT cookie and prime axios defaults */
  bootstrapAuth(apis?: AxiosInstance[]): Promise<boolean>;

  /** Clear in-memory AT (and optionally broadcast to other tabs) */
  clearAccessToken(opts?: { broadcast?: boolean }): void;

  /** Programmatic logout (uses the same authBaseURL you configured) */
  logout(apisToClear?: AxiosInstance[]): Promise<void>;

  /** Replace default redirect with your own (e.g., SPA router navigate) */
  setRedirect(fn: (reason: Reason) => void): void;

  /** Current access token (if any) */
  readonly accessToken: string | null;

}

export function createAuthPlugin(opts: AuthPluginOptions): AuthPlugin {
  const authOrigin = new URL(opts.authBaseURL).origin;
  const SKIP_PATHS = opts.skipPaths ?? [
    '/auth/login',
    '/auth/token/refresh',
    '/auth/logout',
    '/auth/logout-all',
  ];

  // ---- per-plugin state ----
  const state = {
    at: null as string | null,
    expMs: 0,
    refreshPromise: null as Promise<string> | null,
  };

  // default redirect: go to loginURL?redirect=here
  let redirectImpl = (reason: Reason) => {
    const loginURL = opts.loginURL;
    if (!loginURL) {
      console.warn(`[auth] redirect(${reason}) requested but no loginURL was provided`);
      return;
    }
    const here = window.location.href;
    const url = `${loginURL}/login${loginURL.includes('?') ? '&' : '?'}redirect=${encodeURIComponent(here)}`;
    window.location.replace(url);
  };

  function setRedirect(fn: (reason: Reason) => void) {
    redirectImpl = fn;
  }

  function redirect(reason: Reason): never {
    redirectImpl(reason);
    throw new axios.Cancel(`Redirect: ${reason}`);
  }

  function getRequestURL(config: AxiosRequestConfig): URL {
    const base = config.baseURL ?? window.location.origin;
    return new URL(config.url || '', base);
  }

  function isAuthServiceRequest(config: AxiosRequestConfig) {
    try {
      const u = getRequestURL(config);
      return u.origin === authOrigin;
    } catch {
      return false;
    }
  }

  function getPathname(config: AxiosRequestConfig) {
    try {
      return getRequestURL(config).pathname || '/';
    } catch {
      return config.url || '/';
    }
  }

  function shouldSkipRequest(config: AxiosRequestConfig) {
    // Per-request switch w/o TS augmentation:
    const hdr = (config.headers || {}) as Record<string, any>;
    if (hdr['X-Skip-Refresh'] === '1') {
      delete hdr['X-Skip-Refresh']; // don’t leak to server
      config.headers = hdr as any;
      return true;
    }
    if (!isAuthServiceRequest(config)) return false;
    const path = getPathname(config);
    return SKIP_PATHS.some((p) => path.startsWith(p));
  }

  function setAuthHeaderOnConfig(config: AxiosRequestConfig, token: string) {
    const h = (config.headers ||= {}) as any;
    if (h?.set) h.set('Authorization', `Bearer ${token}`);
    else h.Authorization = `Bearer ${token}`;
  }

  function setDefaultAuthHeader(api: AxiosInstance, token: string) {
    (api.defaults.headers as any).Authorization = `Bearer ${token}`;
  }

  function decodeJwtExpMs(token: string): number {
    try {
      const [, payload] = token.split('.');
      const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return json?.exp ? json.exp * 1000 : 0;
    } catch {
      return 0;
    }
  }

  function tokenIsFresh(): boolean {
    if (!state.at) return false;
    const skew = 15_000; // 15s margin
    return Date.now() < (state.expMs || 0) - skew;
  }

  function clearAccessToken(opts?: { broadcast?: boolean }) {
    state.at = null;
    state.expMs = 0;
    accessToken = null; // 🔁 keep exported value in sync
    if (opts?.broadcast !== false) {
      try {
        localStorage.setItem('@auth:logout-broadcast', String(Date.now()));
      } catch { }
    }
  }

  // Cross-tab sync (optional)
  try {
    window.addEventListener('storage', (e) => {
      if (e.key === '@auth:logout-broadcast') {
        state.at = null;
        state.expMs = 0;
      }
    });
  } catch { }

  async function ensureAccessToken(): Promise<string> {
    if (tokenIsFresh()) return state.at!;
    if (state.refreshPromise) return state.refreshPromise;

    state.refreshPromise = (async () => {
      // Use dev override if provided — don't touch in-memory token until we have something valid
      let freshToken: string | null = opts.devStaticAccessToken ?? null;

      if (!freshToken) {
        const r = await fetch(`${opts.authBaseURL}/auth/token/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          cache: 'no-store',
        });

        if (!r.ok || r.status === 401) {
          clearAccessToken();
          return redirect('unauthenticated'); // throws axios.Cancel
        }

        const data = await r.json().catch(() => ({} as any));
        freshToken = data?.accessToken ?? null;
        if (!freshToken) {
          clearAccessToken();
          return redirect('unauthenticated');
        }
      }

      // Only update exported + state AFTER we have a valid token
      accessToken = freshToken;
      state.at = freshToken;
      state.expMs = decodeJwtExpMs(freshToken) || Date.now() + 5 * 60_000;
      return freshToken;
    })().finally(() => {
      state.refreshPromise = null;
    });

    return state.refreshPromise;
  }


  const TAG_IDS = new WeakMap<AxiosInstance, number[]>();
  function attachTaggingInterceptor(api: AxiosInstance) {
    const id = api.interceptors.request.use(
      (config) => {
        const ownerId = getSelectedOwnerFromCookie();
        if (ownerId) {
          if (!config.headers) {
            config.headers = {} as any;
          }
          (config.headers as any)["x-owner-id"] = ownerId;
        }
        return config;
      },
      (error: AxiosError) => Promise.reject(error)
    );

    const arr = TAG_IDS.get(api) || [];
    arr.push(id);
    TAG_IDS.set(api, arr);
  }

  function attachAuthInterceptor(api: AxiosInstance): void {
    api.interceptors.request.use(async (config) => {
      if (shouldSkipRequest(config)) return config;

      try {
        const token = await ensureAccessToken();
        setAuthHeaderOnConfig(config, token);
        return config;
      } catch (err) {
        if (axios.isCancel?.(err)) throw err;
        console.warn('[auth] ensureAccessToken failed:', err);
        return redirect('expired');
      }
    });

    api.interceptors.response.use(
      (resp) => resp,
      async (error: AxiosError) => {
        const cfg = error.config as any;

        // On 401, try one silent token refresh + retry before giving up
        if (
          error.response?.status === 401 &&
          !cfg?._authRetry &&
          !shouldSkipRequest(cfg)
        ) {
          cfg._authRetry = true;
          try {
            // Force a real refresh by clearing the stale in-memory token
            state.at = null;
            state.expMs = 0;
            const newToken = await ensureAccessToken();
            setAuthHeaderOnConfig(cfg, newToken);
            return api(cfg); // retry original request with fresh token
          } catch (refreshErr) {
            // Refresh itself failed — genuinely unauthenticated
            if (axios.isCancel?.(refreshErr)) throw refreshErr;
            clearAccessToken();
            redirect('expired');
          }
        }

        return Promise.reject(error);
      }
    );
  }
  async function fetchAndCacheProfile() {
    // must include credentials so the refresh cookie (Domain=.iax.my) is sent
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };

    // Attach current AT if we have a fresh one; do NOT refresh here
    if (state.at && Date.now() < state.expMs - 15_000) {
      headers.Authorization = `Bearer ${state.at}`;
    }
    const res = await axios.get(`${opts.authBaseURL}/auth/me`, { withCredentials: true, headers });
    const data = (res.data?.data ?? res.data) || {};
    // cache only non-sensitive display info
    const profile = {
      id: data._id,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      isSuperAdmin: !!data.isSuperAdmin,
      permissions: data?.role?.permissions ?? [],
      ownerType: data.ownerType,
      memberships: data.memberships
    };
    try { localStorage.setItem('@ind.user', JSON.stringify(profile)); } catch { }
    return profile;
  }

  async function bootstrapAuth(apis: AxiosInstance[] = []): Promise<boolean> {
    try {
      const token = await ensureAccessToken();
      // Prime all provided clients
      for (const api of apis) setDefaultAuthHeader(api, token);

      await fetchAndCacheProfile().catch(() => { /* non-fatal for shell render */ });
      return true;
    } catch (err) {
      if (axios.isCancel?.(err)) throw err;
      console.warn('[auth] bootstrapAuth failed:', err);
      redirect('unauthenticated');
    }
  }

  // inside your plugin implementation
  async function logout(
    apisToClear: AxiosInstance[] = []
  ): Promise<void> {
    try {
      const path = '/auth/logout';
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };

      // Attach current AT if we have a fresh one; do NOT refresh here
      if (state.at && Date.now() < state.expMs - 15_000) {
        headers.Authorization = `Bearer ${state.at}`;
      }

      await fetch(`${opts.authBaseURL}${path}`, {
        method: 'POST',
        credentials: 'include',  // send RT cookie
        headers,
      });

      // Client-side cleanup
      clearAccessToken();
      for (const api of apisToClear) {
        try {
          delete (api.defaults.headers as any).common?.Authorization;
          delete (api.defaults.headers as any).Authorization;
        } catch { }
      }

      redirect('logout'); // or your router-based redirect
    } catch (err) {
      if (axios.isCancel?.(err)) throw err;
      console.warn('[auth] logout failed:', err);
      // Best-effort cleanup anyway
      clearAccessToken();
      for (const api of apisToClear) {
        try {
          delete (api.defaults.headers as any).common?.Authorization;
          delete (api.defaults.headers as any).Authorization;
        } catch { }
      }
      redirect('logout');
    }
  }


  return {
    attachAuthInterceptor,
    attachTaggingInterceptor,
    bootstrapAuth,
    clearAccessToken,
    logout,
    setRedirect,
    get accessToken() {
      return state.at;
    },
  };
}
